import { useEffect } from 'react';
import versionInfo from './version.js';
//import * as Sentry from "@sentry/react";

function keepFirstDot(str: string) {
  const firstDotIndex = str.indexOf('.');
  if (firstDotIndex === -1) return str; // No dots found

  // Keep everything up to and including the first dot
  const before = str.slice(0, firstDotIndex + 1);
  const after = str.slice(firstDotIndex + 1).replace(/\./g, ''); // Remove all other dots

  return before + after;
}

/**
 * Component to check and automatically reload when a new version is available
 * Only works with installed PWA
 */
const VersionChecker: React.FC = () => {
  useEffect(() => {
    // Check if the app is running in PWA environment
    const checkIfPwa = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInPwaMode = isStandalone || 
                          (window.navigator as any).standalone || 
                          document.referrer.includes('android-app://');
      return isInPwaMode;
    };

    // Function to check version and automatically reload when needed
    const checkVersion = () => {
      try {

        // Get current version from package.json
        const currentPJsonVersion = versionInfo.version;
        //console.log(`[PWA Version] Current p.json version: ${currentPJsonVersion}`);

        // Get current version from window runtime environment    
        const currentWindowVersion = window.__RUNTIME_CONFIG__.REACT_APP_VERSION;
        //console.log(`[PWA Version] Current window version: ${currentWindowVersion}`);

        // window has precedence over package.json
        const currentVersion = currentWindowVersion
        // Only check if running in PWA
        //console.log(`isPWA? ${checkIfPwa()}`);
        //if (!checkIfPwa()) return; // todo
        
        // Get saved version from localStorage
        const storedVersion = localStorage.getItem('pwa_version');
        /*
        Sentry.captureMessage("Frontend version check", {user: {
          id: localStorage.userid}, 
          tags: { 
            'FrontendStoredVersion': storedVersion,
            'FrontendCurrentPJsonVersion': currentPJsonVersion,
            'FrontendCurrentWindowVersion': currentWindowVersion,
            'FrontendIsPWA': checkIfPwa(),
            'FrontendScreenResolution': `${window.screen.width}x${window.screen.height}`,            
            'FrontendNavUserAgent': navigator.userAgent,
            'FrontendNavLanguage': navigator.language,
            'FrontendNavPlatform': navigator.platform,
            'FrontendTimezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            'FrontendDocumentRef': document.referrer
          }
        });
        */
        
        //console.log(`[PWA Version] Stored: ${storedVersion}, Current: ${currentVersion}`);
        
        // If no saved version, save the current version
        if (!storedVersion) {
          //console.log(`[PWA Version] First run, saving current version: ${currentVersion}`);
          localStorage.setItem('pwa_version', currentVersion);
          return;
        }
      
        // Compare versions
        if (storedVersion !== currentVersion) {
          //console.log(`[PWA Version] New version detected: j: ${currentPJsonVersion} w: ${currentWindowVersion}.`);

          if(window.location.href.includes('dashboard/stories')) {
            //console.log("Safe to reload now");
          } else {
            //console.log("Not safe to reload now. Do nothing.");
            return
          }
          
          console.log("Reloading application...");
          
          // Update version in localStorage
          localStorage.setItem('pwa_version', currentVersion);
          
          // Reload page to update
          //window.location.reload(); // todo
          window.location.href = window.location.pathname + '?reload=' + new Date().getTime();
        }
      } catch (error) {
        console.error('[PWA Version] Error checking version:', error);
      }
    };
    
    // Check when component mounts
    checkVersion();
    
    // Set up periodic check every 30 minutes
    const interval = setInterval(checkVersion, 30 * 60 * 1000);
    
    // Check when app returns from background
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // This component doesn't render anything
  return null;
};

// Add test function to window to call from console
// This helps test the feature without being in a real PWA environment
(window as any).testPwaVersionCheck = (forceReload = false) => {
  // Get current version
  const currentVersion = versionInfo.version;
  
  // Display package info
  console.log(`[PWA Version Test] Current version: ${currentVersion}`);
  
  // Simulate older version
  const simulateOldVersion = () => {
    const parts = currentVersion.split('.');
    const lastPart = parseInt(parts[parts.length - 1], 10);
    parts[parts.length - 1] = (lastPart > 0 ? lastPart - 1 : 0).toString();
    return parts.join('.');
  };
  
  // Get saved version
  const storedVersion = localStorage.getItem('pwa_version');
  
  // Display information
  console.log(`[PWA Version Test] Current version: ${currentVersion}`);
  console.log(`[PWA Version Test] Stored version: ${storedVersion}`);
  
  // If forcing reload, save old version
  if (forceReload) {
    const oldVersion = simulateOldVersion();
    localStorage.setItem('pwa_version', oldVersion);
    console.log(`[PWA Version Test] Simulated old version: ${oldVersion}`);
    console.log(`[PWA Version Test] Reload browser to test...`);
    return;
  }
  
  // If simulating PWA and triggering version check
  // Override checkIfPwa to always return true
  const originalMatchMedia = window.matchMedia;
  window.matchMedia = (query: string) => {
    if (query === '(display-mode: standalone)') {
      return { matches: true } as MediaQueryList;
    }
    return originalMatchMedia(query);
  };
  
  // Trigger check by simulating visibility change event
  console.log(`[PWA Version Test] Triggering version check...`);
  const event = new Event('visibilitychange');
  document.dispatchEvent(event);
  
  // Restore matchMedia after 5 seconds
  setTimeout(() => {
    window.matchMedia = originalMatchMedia;
    console.log(`[PWA Version Test] Restored matchMedia`);
  }, 5000);
};

export default VersionChecker; 